import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'
import { cigarDetailApi } from './cigarDetailApi'

export interface WishlistCigar {
  id: string
  name: string
  description?: string
  strengthLevel?: string
  wrapper?: string
  averagePrice?: number
  images?: string[]
  brand: { id: string; name: string }
  cigarLine: { id: string; name: string }
  vitola: { id: string; name: string }
  cigarStatistics?: Array<{ averageRating: number; totalReviews: number }>
}

export interface WishlistItem {
  id: string
  userId: string
  communityCigarId: string
  notes?: string
  createdAt: string
  updatedAt: string
  communityCigar: WishlistCigar
}

export interface WishlistResponse {
  items: WishlistItem[]
  pagination: {
    page: number
    limit: number
    totalCount: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
}

export const wishlistApi = createApi({
  reducerPath: 'wishlistApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/wishlist',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['Wishlist'],
  endpoints: (builder) => ({
    getWishlist: builder.query<WishlistResponse, { page?: number; limit?: number } | void>({
      query: (params) => ({
        url: '/',
        params: params || {},
      }),
      transformResponse: (response: { success: boolean; data: WishlistResponse }) => response.data,
      providesTags: ['Wishlist'],
    }),

    removeFromWishlist: builder.mutation<void, string>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Wishlist'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(cigarDetailApi.util.invalidateTags(['CigarStatus']))
        } catch { /* mutation failed */ }
      },
    }),

    moveToCollection: builder.mutation<void, string>({
      query: (id) => ({
        url: `/${id}/move-to-collection`,
        method: 'POST',
      }),
      invalidatesTags: ['Wishlist'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(cigarDetailApi.util.invalidateTags(['CigarStatus']))
        } catch { /* mutation failed */ }
      },
    }),
  }),
})

export const {
  useGetWishlistQuery,
  useRemoveFromWishlistMutation,
  useMoveToCollectionMutation,
} = wishlistApi
